import { ActionIcon, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconAlertTriangle, IconEye } from "@tabler/icons-react";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import type { PPAInvoice, PPAInvoiceDrift } from "../../PPAInvoicePage.types";

const RELEVANT_DRIFT_COLOR = "var(--mantine-color-warning-filled)";
const MINOR_DRIFT_COLOR = "var(--mantine-color-gray-5)";
const ICON_SIZE = 20;

const HAS_DRIFTS_LABEL = "Abweichungen vorhanden";
const NO_DRIFTS_LABEL = "Keine Abweichungen";

function formatEuro(value: string | number): string {
  return Number(value).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR"
  });
}

function buildDriftTooltip(drift: PPAInvoiceDrift): React.ReactNode {
  const lines: Array<string> = [];

  const diff = Number(drift.amount_due_difference);
  if (diff !== 0) {
    const sign = diff > 0 ? "+" : "";
    lines.push(
      `Bruttobetrag geändert: ${formatEuro(drift.previous_amount_due)} → ${formatEuro(drift.new_amount_due)} (Differenz: ${sign}${formatEuro(diff)})`
    );
  }

  if (drift.has_line_items_added_or_removed) {
    lines.push("Rechnungspositionen wurden hinzugefügt oder entfernt");
  }

  if (drift.has_kwh_amount_change && drift.has_price_amount_change) {
    lines.push("Energiemengen und Preise haben sich geändert");
  } else if (drift.has_kwh_amount_change) {
    lines.push("Energiemengen haben sich geändert");
  } else if (drift.has_price_amount_change) {
    lines.push("Preise haben sich geändert");
  }

  return (
    <Stack gap={4}>
      {lines.map((line) => (
        <Text key={line} size="sm">
          {line}
        </Text>
      ))}
    </Stack>
  );
}

export function getDriftsColumn(
  invoiceDrifts: Map<string, PPAInvoiceDrift> | undefined,
  onDownloadDraft: (identityKey: string) => void,
  isLoadingDraft: (identityKey: string) => boolean
): MRT_ColumnDef<PPAInvoice> {
  return {
    id: "invoice_drifts",
    accessorFn: (row) =>
      invoiceDrifts?.has(row.unique_identifier)
        ? HAS_DRIFTS_LABEL
        : NO_DRIFTS_LABEL,
    header: "Hinweise",
    size: 140,
    enableSorting: false,
    filterVariant: "multi-select",
    mantineFilterMultiSelectProps: {
      data: [HAS_DRIFTS_LABEL, NO_DRIFTS_LABEL]
    },
    Cell: ({ row }) => {
      const drift = invoiceDrifts?.get(row.original.unique_identifier);

      if (!drift) {
        return null;
      }

      const iconColor = drift.amount_due_change_is_relevant
        ? RELEVANT_DRIFT_COLOR
        : MINOR_DRIFT_COLOR;

      return (
        <Group gap="xs" wrap="nowrap">
          <Tooltip label={buildDriftTooltip(drift)} multiline w={320}>
            <IconAlertTriangle
              color={iconColor}
              size={ICON_SIZE}
              style={{ flexShrink: 0 }}
            />
          </Tooltip>
          <Tooltip label="Vorschau mit aktuellen Daten">
            <ActionIcon
              aria-label="Vorschau mit aktuellen Daten herunterladen"
              loading={isLoadingDraft(drift.identity_key)}
              variant="transparent"
              onClick={() => onDownloadDraft(drift.identity_key)}
            >
              <IconEye color="#333" />
            </ActionIcon>
          </Tooltip>
        </Group>
      );
    }
  };
}
